<template>
  <v-list-item
    ref="itemRef"
    :class="{
      'ui-menu-content-item': true,
      'ui-menu-content-item--selectable': true,
      'ui-menu-content-item--selected': selected && item.key === selected,
      'ui-menu-content-item--disabled': item.disabled,
    }"
    :href="item.routerLink ? undefined : item.href"
    :target="item.routerLink ? undefined : item.target"
    v-bind="item.routerLink ? { to: item.href } : {}"
    :ripple="false"
    @click.stop="handleItemClick"
  >
    <div class="ui-menu-content-item__container">
      <slot name="item" :item="item">
        <ui-icon
          v-if="item.icon"
          class="ui-menu-content-item__icon ml-4 ml-first-0"
          :color="item.disabled ? $colors.gray40 : item.iconColor"
          :icon="item.icon"
        ></ui-icon>

        <span
          class="ui-menu-content-item__text ml-4 ml-first-0"
          :style="{ color: item.color }"
        >
          {{ item.title }}
        </span>

        <ui-icon
          v-if="item.iconAppend"
          class="ui-menu-content-item__icon ml-4 ml-first-0"
          :color="
            typeof item.iconAppendColor === 'string' ? item.iconAppendColor : undefined
          "
          :icon="item.iconAppend"
        ></ui-icon>

        <div v-if="item.subtitle" class="ui-menu-content-item__subtitle ml-4 ml-first-0">
          {{ item.subtitle }}
        </div>

        <ui-plan-limits
          v-if="showPlanLimitsIcon"
          class="plan-limits-icon ml-4"
        ></ui-plan-limits>
      </slot>

      <ui-chevron
        v-if="showRowChevron"
        class="ml-4 ml-first-0"
        :color="typeof item.color === 'string' ? item.color : undefined"
        :open="chevronOpen"
      ></ui-chevron>

      <ui-menu
        v-if="hasSubItems"
        v-model="menu"
        ref="menuRef"
        :has-triangle="false"
        location="bottom start"
        :offset="[0, 0]"
        :target="itemTarget || undefined"
        :items="item.subItems"
        :submenu="true"
        :filter="item.meta && item.meta.filter"
        :menu-props="menuProps"
        @action="handleAction"
      >
        <template #activator="{ props: activatorProps }">
          <span
            v-bind="activatorProps"
            class="ui-menu-content-item__submenu-anchor"
          />
        </template>
      </ui-menu>
    </div>
  </v-list-item>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useColors, usePlanLimits } from '@/composables';

const props = withDefaults(
  defineProps<{
    item?: Bemob.ListItem;
    menuProps?: Record<string, unknown>;
    selected?: string;
    activatorChevron?: boolean;
    menuOpen?: boolean;
  }>(),
  {
    activatorChevron: false,
    menuOpen: false,
  },
);

const emit = defineEmits<{
  (e: 'action', item: Bemob.ListItem): void;
}>();

const menu = ref(false);
const menuRef = ref<{ toggle: () => void } | null>(null);
const itemRef = ref<{ $el?: Element } | Element | null>(null);
const itemTarget = computed(() => {
  const el = itemRef.value;
  if (!el) return null;
  if (el instanceof Element) return el;
  return el.$el instanceof Element ? el.$el : null;
});
const $colors = useColors();
const item = computed(() => props.item ?? ({} as Bemob.ListItem));
const menuProps = computed(() => props.menuProps ?? {});
const selected = computed(() => props.selected ?? '');

const itemPlanLimits = computed(() => props.item?.planLimits);
const { showPlanLimitsIcon, handleClickPlanLimits, hasPlanLimitsRestriction } =
  usePlanLimits(
    () => itemPlanLimits.value,
    () => Boolean(props.menuProps?.planLimitsEditable),
  );

const hasSubItems = computed(
  () => Array.isArray(props.item?.subItems) && props.item.subItems.length > 0,
);
const showRowChevron = computed(() => hasSubItems.value || props.activatorChevron);
const chevronOpen = computed(() =>
  props.activatorChevron ? props.menuOpen : menu.value,
);

const handleItemClick = (event?: MouseEvent) => {
  if (hasSubItems.value) {
    menuRef.value?.toggle();
    return;
  }
  if (props.item?.disabled) {
    return;
  }

  const limits = itemPlanLimits.value;
  // Check at click time so Add condition / menu rows can't bypass plan limits
  if (limits && hasPlanLimitsRestriction(limits)) {
    event?.preventDefault();
    event?.stopPropagation();
    handleClickPlanLimits(limits);
    emit('action', props.item!);
    return;
  }

  props.item?.click?.(event);
  if (props.item) {
    emit('action', props.item);
  }
};

const handleAction = (actionItem: Bemob.ListItem) => {
  emit('action', actionItem);
};
</script>

<style lang="scss" scoped>
.plan-limits-icon {
  position: relative;
  top: 2px;
}
</style>

<style lang="scss">
.ui-menu-content-item {
  width: 100%;
  font-size: 13px;
  position: relative;

  &__submenu-anchor {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  &.v-list-item.v-list-item--density-default.v-list-item--one-line:not(
      .v-list-item--nav
    ) {
    padding: 0 8px;
    padding-inline: 8px;
    min-height: 32px;
    height: unset;
    color: var(--color-graphite);
    align-items: center;
  }

  &.text-primary {
    color: var(--color-graphite) !important;
  }

  &--selectable {
    &:hover {
      background-color: var(--color-green10);
    }
  }

  & + & {
    border-top: 1px solid var(--color-gray10);
  }

  &--disabled {
    cursor: default;
    color: var(--color-gray40);

    &:hover {
      background-color: var(--color-white);
    }
  }

  &__container {
    display: flex;
    align-items: baseline;
    flex-grow: 1;
    overflow: hidden;
  }

  &__subtitle {
    font-size: 13px;
    font-weight: bold;
    white-space: nowrap;
  }

  & &__icon {
    font-size: 13px;
    color: inherit;
  }

  &__text {
    font-size: 13px;
    color: inherit;
    flex-grow: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-transform: capitalize;
    text-align: left;

    @at-root #{selector-append('a', &)} {
      text-decoration: none;
    }
  }

  &--selected {
    background-color: var(--color-green10);
    color: var(--color-green);
  }
}
</style>
